/* Visual copies loop seamlessly; only original links enter the tab order. */
if (!customElements.get('bc-occasion-slider')) customElements.define('bc-occasion-slider', class extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loop !== 'true') return;
    // Below-fold loops need neither cloned images nor layout work at page load.
    this.prepare = new IntersectionObserver(entries => {
      if (!entries[0].isIntersecting) return;
      this.prepare.disconnect();
      this.initialize();
    }, { rootMargin: '250px' });
    this.prepare.observe(this);
  }
  initialize() {
    this.focused = this.contains(document.activeElement);
    this.track = this.querySelector('ul');
    this.originals = [...this.track.children];
    if (this.originals.length < 2) return;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.copies = [];
    for (const side of ['before', 'after']) {
      const fragment = document.createDocumentFragment();
      this.originals.forEach(item => {
        const copy = item.cloneNode(true);
        copy.removeAttribute('id');
        copy.removeAttribute('data-shopify-editor-block');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('a').forEach(a => a.tabIndex = -1);
        this.copies.push(copy);
        fragment.append(copy);
      });
      if (side === 'before') this.track.prepend(fragment);
      else this.track.append(fragment);
    }
    this.resize = new ResizeObserver(() => {
      this.cycle = this.originals[0].offsetLeft - this.track.firstElementChild.offsetLeft;
      this.position = this.cycle;
      this.track.scrollLeft = this.position;
    });
    this.resize.observe(this.track);
    this.track.addEventListener('dragstart', event => event.preventDefault(), options);
    this.track.addEventListener('pointerdown', event => {
      if (event.button !== 0) return;
      this.suppressClick = false;
      this.drag = { id: event.pointerId, x: event.clientX, last: event.clientX, moved: false };
      this.position = this.track.scrollLeft;
    }, options);
    this.track.addEventListener('pointermove', event => {
      if (!this.drag || event.pointerId !== this.drag.id) return;
      if (Math.abs(event.clientX - this.drag.x) > 6) {
        this.drag.moved = true;
        this.track.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      if (this.drag.moved) {
        this.position += this.drag.last - event.clientX;
        this.wrap();
        this.track.scrollLeft = this.position;
      }
      this.drag.last = event.clientX;
    }, options);
    const release = () => {
      if (!this.drag) return;
      this.suppressClick = this.drag.moved;
      this.drag = null;
      this.classList.remove('is-dragging');
    };
    window.addEventListener('pointerup', release, options);
    this.track.addEventListener('pointercancel', release, options);
    this.track.addEventListener('click', event => {
      if (this.suppressClick) { event.preventDefault(); event.stopPropagation(); this.suppressClick = false; }
    }, { ...options, capture: true });
    this.addEventListener('pointerenter', () => { this.hovering = true; }, options);
    this.addEventListener('pointerleave', () => { this.hovering = false; }, options);
    this.addEventListener('focusin', () => { this.focused = true; }, options);
    this.addEventListener('focusout', event => {
      this.focused = this.contains(event.relatedTarget);
      this.position = this.track.scrollLeft;
    }, options);
    const frame = time => {
      this.frame = null;
      const elapsed = Math.min(time - (this.lastTime || time), 50);
      this.lastTime = time;
      if (this.cycle && this.dataset.autoplay === 'true' && this.visible && !document.hidden && !this.motion.matches && !this.hovering && !this.focused && !this.drag) {
        this.position += elapsed * .032;
        this.wrap();
        this.track.scrollLeft = this.position;
      }
      this.schedule();
    };
    this.schedule = () => {
      const active = this.visible && !document.hidden && !this.motion.matches && this.dataset.autoplay === 'true';
      if (active && !this.frame) this.frame = requestAnimationFrame(frame);
      if (!active) {
        cancelAnimationFrame(this.frame);
        this.frame = null;
        this.lastTime = null;
      }
    };
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.schedule();
    });
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.schedule, options);
    this.motion.addEventListener('change', this.schedule, options);
  }
  wrap() {
    if (this.cycle) this.position = this.cycle + ((this.position - this.cycle) % this.cycle + this.cycle) % this.cycle;
  }
  disconnectedCallback() {
    this.prepare?.disconnect();
    this.abort?.abort(); this.resize?.disconnect(); this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.lastTime = null;
    this.copies?.forEach(copy => copy.remove());
  }
});
